import { useEffect, useMemo, useState } from "react";
import { Plus, PencilSimple, Trash, Play, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { SOURCE_META, formatPeriod } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const PY_EXAMPLE = `# Variables disponibles: env, company_id, planta,
# fecha_inicio, fecha_fin, mes, anio, periodo
orders = env['sale.order'].search([
    ('state', '=', 'sale'),
    ('date_order', '>=', fecha_inicio),
    ('date_order', '<=', fecha_fin),
])
return sum(orders.mapped('amount_total'))`;

const EMPTY = {
  code: "", name: "", unit: "", description: "", category: "", source_type: "manual", active: true,
  auto_type: "query", python_code: "",
  odoo_mapping: { model: "", method: "count", field: "", date_field: "", domain: "" },
};

export default function Variables() {
  const { hasRole } = useAuth();
  const { companies, periods } = useApp();
  const isAdmin = hasRole("admin");
  const [variables, setVariables] = useState([]);
  const [category, setCategory] = useState("all");
  const [source, setSource] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [testCompany, setTestCompany] = useState("");
  const [testPeriod, setTestPeriod] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/variables").then((res) => setVariables(res.data));
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => Array.from(new Set(variables.map((v) => v.category).filter(Boolean))), [variables]);
  const filtered = variables.filter(
    (v) => (category === "all" || v.category === category) && (source === "all" || v.source_type === source)
  );

  const resetTest = () => {
    setTestResult(null);
    setTestCompany((prev) => prev || companies[0]?.id || "");
    setTestPeriod((prev) => prev || periods[0] || "");
  };

  const openNew = () => { setForm(EMPTY); setEditing(null); resetTest(); setOpen(true); };
  const openEdit = (v) => {
    const m = v.odoo_mapping || {};
    setForm({
      ...v,
      auto_type: v.auto_type || "query",
      python_code: v.python_code || "",
      odoo_mapping: {
        model: m.model || "", method: m.method || "count", field: m.field || "",
        date_field: m.date_field || "",
        domain: m.domain ? JSON.stringify(m.domain) : "",
      },
    });
    setEditing(v.id);
    resetTest();
    setOpen(true);
  };

  const isPython = form.source_type === "odoo" && form.auto_type === "python";

  // Build the Odoo mapping object from the form (shared by save & test).
  const buildMapping = () => {
    if (form.source_type === "manual" || isPython || !form.odoo_mapping?.model) return null;
    let domain = [];
    const raw = (form.odoo_mapping.domain || "").trim();
    if (raw) {
      try { domain = JSON.parse(raw); }
      catch { toast.error("El dominio Odoo debe ser JSON válido, ej: [[\"state\",\"=\",\"done\"]]"); return undefined; }
    }
    return {
      model: form.odoo_mapping.model,
      method: form.odoo_mapping.method || "count",
      field: form.odoo_mapping.field || "",
      date_field: form.odoo_mapping.date_field || "",
      domain,
    };
  };

  const probar = async () => {
    if (!testCompany) { toast.error("Selecciona una planta para la prueba."); return; }
    if (!testPeriod) { toast.error("Selecciona un mes para la prueba."); return; }
    let payload = { company_id: testCompany, period: testPeriod, auto_type: form.auto_type };
    if (isPython) {
      if (!(form.python_code || "").trim()) { toast.error("Escribe el código Python."); return; }
      payload.python_code = form.python_code;
    } else {
      const mapping = buildMapping();
      if (mapping === undefined) return; // invalid JSON, toast already shown
      if (!mapping?.model) { toast.error("Configura al menos el modelo de Odoo."); return; }
      payload.odoo_mapping = mapping;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/odoo/variable/test", payload);
      setTestResult({ ok: true, value: data.value, company: data.company, config: data.config });
    } catch (e) {
      setTestResult({ ok: false, error: formatApiError(e.response?.data?.detail) });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    const mapping = buildMapping();
    if (mapping === undefined) return; // invalid JSON
    const payload = {
      code: form.code, name: form.name, unit: form.unit, description: form.description || "",
      category: form.category, source_type: form.source_type, active: form.active,
      company_id: form.company_id ?? null,
      auto_type: form.source_type === "odoo" ? form.auto_type : "query",
      python_code: isPython ? (form.python_code || "") : null,
      odoo_mapping: mapping,
    };
    try {
      if (editing) await api.put(`/variables/${editing}`, payload);
      else await api.post("/variables", payload);
      toast.success(editing ? "Variable actualizada" : "Variable creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const remove = async (v) => {
    if (!window.confirm(`¿Eliminar la variable "${v.name}"?`)) return;
    await api.delete(`/variables/${v.id}`);
    toast.success("Variable eliminada");
    load();
  };

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Variables</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Orígenes de datos: Manual · Automático (Odoo) — base del flujo de cálculo
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los orígenes</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="odoo">Automático (Odoo)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 w-[210px]" data-testid="variable-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button onClick={openNew} data-testid="new-variable-button">
              <Plus size={18} className="mr-1" aria-hidden="true" /> Nueva variable
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm" data-testid="variables-table">
          <thead className="bg-accent/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Variable</th>
              <th className="px-4 py-3">Código</th>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3">Unidad</th>
              <th className="px-4 py-3">Origen</th>
              {isAdmin && <th className="px-4 py-3 text-right">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => {
              const sm = SOURCE_META[v.source_type] || SOURCE_META.manual;
              return (
                <tr key={v.id} className="border-t border-border hover:bg-accent/30" data-testid={`variable-row-${v.code}`}>
                  <td className="px-4 py-2.5 font-medium">{v.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{v.code}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{v.category}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{v.unit}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className={`${sm.bg} ${sm.text} ${sm.border}`}>{sm.label}</Badge>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(v)} data-testid={`edit-variable-${v.code}`}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(v)} data-testid={`delete-variable-${v.code}`}>
                          <Trash size={16} className="text-danger" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="variable-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar variable" : "Nueva variable"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Código</Label>
                <Input value={form.code} disabled={!!editing} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="total_empleados" data-testid="variable-code-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Unidad</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="personas, MXN…" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="variable-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoría</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Origen de datos</Label>
                <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                  <SelectTrigger data-testid="variable-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="odoo">Automático (Odoo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.source_type !== "manual" && (
              <div className="space-y-3 rounded-lg border border-warning/30 bg-warning/5 p-3" data-testid="odoo-mapping-section">
                <div className="space-y-1.5">
                  <Label>Tipo de Origen</Label>
                  <Select value={form.auto_type} onValueChange={(v) => setForm({ ...form, auto_type: v })}>
                    <SelectTrigger data-testid="variable-autotype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="query">Consulta Odoo (modelo / método / campo)</SelectItem>
                      <SelectItem value="python">Código Python (script ORM)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {!isPython && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Modelo Odoo</Label>
                        <Input
                          value={form.odoo_mapping?.model || ""}
                          onChange={(e) => setForm({ ...form, odoo_mapping: { ...form.odoo_mapping, model: e.target.value } })}
                          placeholder="sale.order"
                          data-testid="odoo-map-model"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Método</Label>
                        <Select
                          value={form.odoo_mapping?.method || "count"}
                          onValueChange={(v) => setForm({ ...form, odoo_mapping: { ...form.odoo_mapping, method: v } })}
                        >
                          <SelectTrigger data-testid="odoo-map-method"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="count">Conteo de registros</SelectItem>
                            <SelectItem value="sum">Suma de campo</SelectItem>
                            <SelectItem value="avg">Promedio de campo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {form.odoo_mapping?.method !== "count" && (
                      <div className="space-y-1.5">
                        <Label>Campo a agregar</Label>
                        <Input
                          value={form.odoo_mapping?.field || ""}
                          onChange={(e) => setForm({ ...form, odoo_mapping: { ...form.odoo_mapping, field: e.target.value } })}
                          placeholder="amount_total"
                          data-testid="odoo-map-field"
                        />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label>Campo de fecha (para filtrar por periodo)</Label>
                      <Input
                        value={form.odoo_mapping?.date_field || ""}
                        onChange={(e) => setForm({ ...form, odoo_mapping: { ...form.odoo_mapping, date_field: e.target.value } })}
                        placeholder="date_order"
                        data-testid="odoo-map-datefield"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Dominio (JSON, opcional)</Label>
                      <Textarea
                        value={form.odoo_mapping?.domain || ""}
                        onChange={(e) => setForm({ ...form, odoo_mapping: { ...form.odoo_mapping, domain: e.target.value } })}
                        placeholder='[["state","=","done"]]'
                        rows={2}
                        className="font-mono text-xs"
                        data-testid="odoo-map-domain"
                      />
                    </div>
                  </>
                )}

                {isPython && (
                  <div className="space-y-1.5" data-testid="python-editor-section">
                    <div className="flex items-center justify-between">
                      <Label>Código Python (ORM Odoo · solo lectura)</Label>
                      {!form.python_code && (
                        <button type="button" className="text-xs text-info hover:underline"
                          onClick={() => setForm({ ...form, python_code: PY_EXAMPLE })} data-testid="python-insert-example">
                          Insertar ejemplo
                        </button>
                      )}
                    </div>
                    <div className="overflow-hidden rounded-md border border-border" data-testid="python-code-editor">
                      <CodeMirror
                        value={form.python_code || ""}
                        height="200px"
                        theme="dark"
                        extensions={[python()]}
                        onChange={(val) => setForm((f) => ({ ...f, python_code: val }))}
                        basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true }}
                      />
                    </div>
                    <p className="font-mono text-[11px] text-muted-foreground">
                      Disponibles: env, company_id, planta, fecha_inicio, fecha_fin, mes, anio, periodo · usa <strong>return</strong> para el valor.
                    </p>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  {isPython
                    ? "El script se ejecuta bajo demanda al consultar el dashboard/históricos, con los filtros (planta + periodo) seleccionados."
                    : "La consulta a Odoo se ejecuta bajo demanda al consultar el dashboard/históricos, filtrada por la compañía Odoo de la planta seleccionada."}
                </p>

                <div className="space-y-2 rounded-lg border border-border bg-card p-3" data-testid="odoo-test-section">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">Probar consulta</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Planta</Label>
                      <Select value={testCompany} onValueChange={setTestCompany}>
                        <SelectTrigger className="h-9" data-testid="odoo-test-company"><SelectValue placeholder="Planta" /></SelectTrigger>
                        <SelectContent>
                          {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Mes</Label>
                      <Select value={testPeriod} onValueChange={setTestPeriod}>
                        <SelectTrigger className="h-9" data-testid="odoo-test-period"><SelectValue placeholder="Mes" /></SelectTrigger>
                        <SelectContent>
                          {periods.map((p) => (<SelectItem key={p} value={p}>{formatPeriod(p)}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={probar} disabled={testing} data-testid="odoo-test-run" className="w-full">
                    {testing ? <CircleNotch size={15} className="mr-1 animate-spin" aria-hidden="true" /> : <Play size={15} className="mr-1" aria-hidden="true" />}
                    Probar
                  </Button>
                  {testResult && (
                    testResult.ok ? (
                      <div className="space-y-1.5 rounded-md bg-success/10 px-3 py-2 text-sm" data-testid="odoo-test-result">
                        <div className="flex items-center gap-2 text-success">
                          <CheckCircle size={16} aria-hidden="true" />
                          <span>Resultado{testResult.company ? ` (${testResult.company})` : ""}: <strong>{testResult.value}</strong></span>
                        </div>
                        {testResult.config && (
                          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pl-6 font-mono text-[11px] text-muted-foreground">
                            <span>modelo: {testResult.config.model}</span>
                            <span>método: {testResult.config.method}</span>
                            {testResult.config.field && <span>campo: {testResult.config.field}</span>}
                            {testResult.config.date_field && <span>fecha: {testResult.config.date_field}</span>}
                            <span className="col-span-2 truncate" title={JSON.stringify(testResult.config.domain)}>
                              dominio: {JSON.stringify(testResult.config.domain)}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="odoo-test-result">
                        <XCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{testResult.error}</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-variable-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
